import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Field from "../../../components/v3/Field";
import Btn from "../../../components/v3/Btn";
import { drApi } from "../../../services/drApi";
import { apiErrorMessage, apiStatus } from "../../../utils/apiError";
import { personName } from "../../DR/Approvals/ApprovalContextCards";

/**
 * ComplianceDecisionList - every decision a reviewer has recorded.
 *
 * This is the surface that makes UDP > CCO > DR real: without a list of what
 * was decided, by whom, and whether anyone has looked at it since, "a CCO can
 * review or reverse a DR's decision" is a promise with no way to keep it.
 *
 * A Platform Manager reads this list but decides nothing on it, which is why
 * the only action offered here is Open.
 */

const TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "KYC", label: "KYC" },
  { value: "CLASSIFICATION", label: "Classification" },
  { value: "SUITABILITY", label: "Suitability" },
];

const DECISION_OPTIONS = [
  { value: "", label: "All decisions" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "NEEDS_INFO", label: "Needs information" },
];

// The API's vocabulary, not ours: unreviewed rows are matched with NONE (or
// NULL), and REVERSED selects rows that HAVE a reversal. Sending the plausible
// "UNREVIEWED" matched nothing and the filter looked like an empty result set
// rather than a mistake.
const REVIEW_STATUS_OPTIONS = [
  { value: "", label: "Any review status" },
  { value: "NONE", label: "Unreviewed" },
  { value: "REVIEWED", label: "Reviewed" },
  { value: "FLAGGED", label: "Flagged" },
  { value: "REVERSED", label: "Reversed" },
];

// The decisions list flattens its people through one `person()` helper, and
// spells the investor with a LOWERCASE key: `investor`, not `Investor` like
// every other endpoint. Reading the capitalised name found nothing and every
// row rendered a dash where the investor's name belongs.
function investorName(row) {
  const inv = row?.investor || row?.Investor;
  if (!inv) return row?.investor_user_id ? `User #${row.investor_user_id}` : "-";
  const full = `${inv.firstname || ""} ${inv.lastname || ""}`.trim();
  return inv.name || full || inv.email || `User #${inv.id}`;
}

/** The row carries Campaign {id, name, slug}; `campaign_name` is the DB column. */
function campaignLabel(row) {
  const c = row?.Campaign || row?.campaign;
  if (c) return c.name || c.campaign_name || `Campaign ${c.id}`;
  return row?.campaign_id ? `Campaign ${row.campaign_id}` : "-";
}

function ComplianceDecisionList() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    reviewer: "",
    type: "",
    decision: "",
    review_status: "",
  });
  const [reviewers, setReviewers] = useState([]);
  const [reviewersError, setReviewersError] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    drApi
      .reviewers()
      .then((res) => {
        if (!live) return;
        const list = Array.isArray(res) ? res : res?.data;
        setReviewers(Array.isArray(list) ? list : []);
        setReviewersError(false);
      })
      .catch(() => {
        // 404 or 403 on an endpoint still being built must not break the page:
        // the free-text id field below takes over.
        if (live) setReviewersError(true);
      });
    return () => {
      live = false;
    };
  }, []);

  // Every filter change starts a request and none of them cancel. Without a
  // token, a slow first response lands after a fast second one and the table
  // shows rows that do not match the filters on screen.
  const requestRef = useRef(0);

  const fetchDecisions = useCallback(() => {
    const token = ++requestRef.current;
    const current = () => requestRef.current === token;
    setLoading(true);
    const params = { page, limit };
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params[k] = v;
    });
    return drApi
      .decisions(params)
      .then((res) => {
        if (!current()) return;
        const d = res?.data ?? res ?? {};
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 0);
        setError(null);
      })
      .catch((err) => {
        if (!current()) return;
        setRows([]);
        // This endpoint is still being built. A 404 is "nothing here yet", not
        // a failure, and a red banner reading "not_found" tells a reviewer
        // nothing the empty state does not already say.
        setError(apiStatus(err) === 404 ? null : apiErrorMessage(err, "Fetch failed"));
      })
      .finally(() => {
        if (current()) setLoading(false);
      });
  }, [filters, page, limit]);

  useEffect(() => {
    fetchDecisions();
  }, [fetchDecisions]);

  const setFilter = (key) => (e) => {
    const { value } = e.target;
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const subtitle = (
    <>
      <span className="mono">{total}</span>
      <span>decision{total === 1 ? "" : "s"}</span>
      <span>·</span>
      <span className="mono">page {page} of {Math.max(totalPages, 1)}</span>
    </>
  );

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Decision review" subtitle={subtitle} />

      <section className="card">
        <div className="card__body">
          <div className="form-grid form-grid--2col">
            {reviewersError ? (
              <div>
                <Field
                  label="Reviewer user id"
                  name="decision-reviewer"
                  type="number"
                  value={filters.reviewer}
                  onChange={setFilter("reviewer")}
                />
                <span className="field__hint">
                  Reviewer list unavailable, filter by user id.
                </span>
              </div>
            ) : (
              <Field
                as="select"
                label="Reviewer"
                name="decision-reviewer"
                value={filters.reviewer}
                onChange={setFilter("reviewer")}
              >
                <option value="">All reviewers</option>
                {reviewers.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name || r.email || `Reviewer #${r.id}`}
                  </option>
                ))}
              </Field>
            )}

            <Field
              as="select"
              label="Type"
              name="decision-type"
              value={filters.type}
              onChange={setFilter("type")}
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value || "all"} value={o.value}>{o.label}</option>
              ))}
            </Field>

            <Field
              as="select"
              label="Decision"
              name="decision-decision"
              value={filters.decision}
              onChange={setFilter("decision")}
            >
              {DECISION_OPTIONS.map((o) => (
                <option key={o.value || "all"} value={o.value}>{o.label}</option>
              ))}
            </Field>

            <Field
              as="select"
              label="Review status"
              name="decision-review-status"
              value={filters.review_status}
              onChange={setFilter("review_status")}
            >
              {REVIEW_STATUS_OPTIONS.map((o) => (
                <option key={o.value || "all"} value={o.value}>{o.label}</option>
              ))}
            </Field>
          </div>
        </div>
      </section>

      {error && <Alert tone="danger">{error}</Alert>}

      {rows.length === 0 ? (
        <Empty>No decisions match these filters.</Empty>
      ) : (
        <section className="card">
          <div className="card__body card__body--flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Investor</th>
                  <th>Type</th>
                  <th>Campaign</th>
                  <th>Decision</th>
                  <th>Decided by</th>
                  <th>Decided at</th>
                  <th>Review status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const goDetail = () => navigate(`/compliance/decisions/${row.id}`);
                  return (
                    <tr
                      key={row.id}
                      tabIndex={0}
                      role="button"
                      aria-label={`Open decision ${row.id}`}
                      onClick={goDetail}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          goDetail();
                        }
                      }}
                    >
                      <td>{investorName(row)}</td>
                      <td className="muted">{row.submission_type || "-"}</td>
                      <td className="muted">{campaignLabel(row)}</td>
                      <td>{row.decision || "-"}</td>
                      <td className="muted">
                        {personName(row.DecidedBy, row.decided_by_user_id)}
                      </td>
                      <td className="muted">
                        {row.decided_at ? new Date(row.decided_at).toLocaleString() : "-"}
                      </td>
                      <td className="muted">{row.review_status || "Unreviewed"}</td>
                      <td className="r">
                        <Btn
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); goDetail(); }}
                        >
                          Open <ChevronRight />
                        </Btn>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <div className="toolbar">
          <Btn size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Btn>
          <span className="mono" style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)" }}>
            Page {page} of {totalPages}
          </span>
          <Btn size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}
    </PageShell>
  );
}

export default ComplianceDecisionList;
